import proj4 from "proj4";
import type { Point } from "../inp/types.ts";
import { WGS84_PROJ4 } from "./crs.ts";

const converterCache = new Map<string, proj4.Converter>();

function converterFor(sourceProj4: string): proj4.Converter {
  let converter = converterCache.get(sourceProj4);
  if (!converter) {
    converter = proj4(sourceProj4, WGS84_PROJ4);
    converterCache.set(sourceProj4, converter);
  }
  return converter;
}

export function toLonLat(point: Point, sourceProj4: string): [number, number] {
  const [lon, lat] = converterFor(sourceProj4).forward([point.x, point.y]);
  return [lon, lat];
}

export function fromLonLat(lon: number, lat: number, sourceProj4: string): Point {
  const [x, y] = converterFor(sourceProj4).inverse([lon, lat]);
  return { x, y };
}

// Keyed by the (source, target) PAIR, not just source — `converterFor`'s own
// cache always targets WGS84, but this one needs an arbitrary target.
const xyConverterCache = new Map<string, proj4.Converter>();

export function reprojectXY(
  x: number,
  y: number,
  sourceProj4: string,
  targetProj4: string
): [number, number] {
  // Not just an optimization — also avoids precision loss a genuine but
  // identity proj4 round-trip could otherwise introduce.
  if (sourceProj4 === targetProj4) return [x, y];
  const key = `${sourceProj4}␟${targetProj4}`;
  let converter = xyConverterCache.get(key);
  if (!converter) {
    converter = proj4(sourceProj4, targetProj4);
    xyConverterCache.set(key, converter);
  }
  const [rx, ry] = converter.forward([x, y]);
  return [rx, ry];
}

const EARTH_RADIUS_M = 6371008.8;

/** For [lon, lat] points in degrees only — a plain Euclidean distance is meaningless there (degrees aren't a linear unit, and the real distance per degree of longitude shrinks toward the poles). */
export function haversineDistanceMeters(a: [number, number], b: [number, number]): number {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}
